import { useCallback, useEffect, useRef, useState } from "react";

import { startV5Tour, type V5TourHandle } from "./v5TourController";
import { V5_TOURS } from "./v5Tours";
import type { V5TourLive, V5TourRoutePatch } from "./v5TourTypes";

/**
 * Arms a V5 demo tour from the URL (`?tour=<id>` + optional `?kiosk=1`).
 *
 * Mounted inside ThreadV5WorkbenchPage (auth and anonymous routes alike). The
 * params are deliberately NOT stripped: reloads restart the tour (kiosk
 * looping survives), and the URL stays copy-pasteable.
 *
 * The tour narrates LIVE data, so it must not arm over a half-loaded
 * workbench (empty grid, chart still streaming). While the stage isn't ready
 * a branded loading veil covers it. Readiness is the REACT `stageReady` signal
 * the workbench computes from its own data state — NOT a DOM-selector poll:
 * the launcher renders inside the workbench tree, so a poll's timing was
 * coupled to the very load it masks and got orphaned across re-renders,
 * stranding the veil for tens of seconds after the data was actually ready.
 * A hard cap starts the tour regardless so a stuck signal can never hang it.
 */

const SETTLE_MS = 500;
const READY_CAP_MS = 12000;

export default function V5TourLauncher({
  tourId,
  kiosk,
  ready,
  stageReady,
  applyRoute,
  live,
}: {
  tourId: string | null;
  kiosk: boolean;
  /** Gate arming until the workbench has its project route resolved. */
  ready: boolean;
  /** The workbench's own "the data this tour narrates is loaded" signal. */
  stageReady: boolean;
  applyRoute: (patch: V5TourRoutePatch) => void;
  live: { current: V5TourLive };
}) {
  const [phase, setPhase] = useState<"idle" | "loading" | "running" | "ended">("idle");
  // `armed` is STATE, not a ref: arming must re-run the start/cap effect (a ref
  // mutation wouldn't), otherwise the safety cap never gets scheduled.
  const [armed, setArmed] = useState(false);
  // Latch readiness: `stageReady` OSCILLATES during the streaming load (data
  // updates re-derive the assets/dashboard between fetches), and a raw
  // dependency on it kept cancelling the settle timer and resetting the cap,
  // so the tour started tens of seconds late (or on the cap). Once the stage
  // has been ready ONCE it stays ready — a settle can then complete.
  const [everReady, setEverReady] = useState(false);
  const handleRef = useRef<V5TourHandle | null>(null);
  const startedRef = useRef(false);

  const tour = tourId ? V5_TOURS[tourId] : undefined;

  // applyRoute/start identities churn as the workbench re-renders on every
  // search-param change (our own route patches trigger them). Read through
  // render-fresh refs so effects depend on stable values only.
  const applyRouteRef = useRef(applyRoute);
  applyRouteRef.current = applyRoute;
  const stageReadyRef = useRef(stageReady);
  stageReadyRef.current = stageReady;

  const start = useCallback(() => {
    if (!tour || startedRef.current) return;
    startedRef.current = true;
    handleRef.current?.destroy();
    setPhase("running");
    handleRef.current = startV5Tour({
      tour,
      kiosk,
      applyRoute: (patch) => applyRouteRef.current(patch),
      live,
      onEnd: () => setPhase("ended"),
    });
  }, [kiosk, live, tour]);
  const startRef = useRef(start);
  startRef.current = start;

  // Arm once: apply the first step's stage route immediately (so the data the
  // tour needs starts loading), veil until ready. Setting `armed` re-runs the
  // start effect below.
  useEffect(() => {
    if (!tour || !ready || armed) return;
    setArmed(true);
    const first = tour.steps[0];
    if (first?.route) applyRouteRef.current(first.route);
    setPhase(stageReadyRef.current ? "running" : "loading");
  }, [ready, tour, armed]);

  // Latch the ready signal (monotone false→true) so downstream timing never
  // gets cancelled by a transient dip back to false.
  useEffect(() => {
    if (stageReady) setEverReady(true);
  }, [stageReady]);

  // Start when the stage has been ready — or when the safety cap fires so a
  // stuck signal can never hang the veil. Keyed on the LATCH, so once ready the
  // settle always completes; before that, the 12s cap is the hard backstop.
  useEffect(() => {
    if (!tour || !armed || startedRef.current) return;
    const delay = everReady ? SETTLE_MS : READY_CAP_MS;
    const t = window.setTimeout(() => startRef.current(), delay);
    return () => window.clearTimeout(t);
  }, [everReady, armed, tour]);

  useEffect(() => () => handleRef.current?.destroy(), []);

  if (!tour) return null;

  if (phase === "loading") {
    return (
      <div className="v5-tour-veil" role="status" aria-live="polite" data-tour="v5-tour-veil">
        <div className="v5-tour-veil-panel">
          <span className="v5-tour-veil-spinner" aria-hidden="true" />
          <h2>{tour.title}</h2>
          <p>
            Loading the live project — the runs, charts and lineage this tour
            walks through are real records, fetched fresh.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "ended" && !kiosk) {
    return (
      <button
        type="button"
        className="v5-tour-restart-chip"
        onClick={() => {
          startedRef.current = false;
          startRef.current();
        }}
        data-tour="v5-tour-restart"
      >
        ↻ Restart “{tour.title}” tour
      </button>
    );
  }

  return null;
}
